import { format } from 'date-fns'
import { useMemo } from 'react'
import { ComparisonLineChart, LINE_CHART_EMPTY_DATA } from 'src/components/analytics/charts/ComparisonLineChart'
import { NoData } from 'src/components/analytics/common/NoData'
import { TrendIndicator } from 'src/components/analytics/common/TrendIndicator'
import { ComparisonLineChartDataType } from 'src/components/analytics/types/comparison-line-chart'
import { ChartSkeleton } from 'src/components/overview/charts/common/Skeleton'
import { getTimeIncrement, sortDataByDate } from 'src/components/overview/charts/common/utils'
import { useWidgetConfig } from 'src/components/overview/charts/common/useWidgetConfig'
import { useChartData } from 'src/components/overview/useChartData'
import { formatDateRange } from 'src/components/sso-analytics/charts/TotalIdentificationRate/parser'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { formatNumberToTwoDecimals } from 'src/lib/utils/analytics'
import { WidgetId, WidgetSections } from 'src/lib/contexts/AnalyticsContext/types'

export default function PrepaidShareChart() {
  const {
    getters: { selectedTimeScale, refreshKey },
    actions: { setAlertDialogState }
  } = useAnalytics()
  const { threshold_alerts_supported } = useWidgetConfig(WidgetSections.payments, WidgetId.payment_prepaid_share)
  const { ref, isLoading, isError, data } = useChartData({
    chartConfig: {
      id: WidgetId.payment_prepaid_share,
      dependencies: {
        endpoints: ['/prepaid-share-timescale', '/prepaid-share']
      }
    },
    refreshKey
  })

  const prepaidShareData: ComparisonLineChartDataType & {
    totalPrepaidSharePercentage: number
    prepaidSharePercentageChange: number
  } = useMemo(() => {
    const rawData = data[0]?.data?.result?.widgetData?.data?.[0]?.data
    const rawComparedData = data[0]?.data?.result?.widgetData?.compared_data?.[0]?.data

    const rawSummary = data[0]?.data?.result?.widgetData?.data?.[0]?.summary
    const rawComparedSummary = data[0]?.data?.result?.widgetData?.compared_data?.[0]?.summary

    const prepaidShareData = formatNumberToTwoDecimals(data[1]?.data?.result?.widgetData?.data || 0)
    const prepaidShareComparedData = formatNumberToTwoDecimals(
      data[1]?.data?.result?.widgetData?.compared_data?.toFixed(2) || 0
    )

    if (rawData && rawData.every((item: any) => item.prepaid_share === 0)) {
      if (rawComparedData && rawComparedData.every((item: any) => item.prepaid_share === 0)) {
        return {
          ...LINE_CHART_EMPTY_DATA,
          totalPrepaidSharePercentage: 0,
          prepaidSharePercentageChange: 0
        }
      }
      return {
        ...LINE_CHART_EMPTY_DATA,
        totalPrepaidSharePercentage: 0,
        prepaidSharePercentageChange: 0
      }
    }

    if (!rawData || rawData.length === 0) {
      return {
        ...LINE_CHART_EMPTY_DATA,
        totalPrepaidSharePercentage: 0,
        prepaidSharePercentageChange: 0
      }
    }

    const currentData = rawData.sort(sortDataByDate)
    let previousData: any[] = []
    if (rawComparedData) {
      previousData = rawComparedData.sort(sortDataByDate)
    }

    const currentRange =
      new Date(currentData[currentData.length - 1].date).getTime() - new Date(currentData[0].date).getTime()
    let previousRange = 0
    if (previousData.length > 0) {
      previousRange =
        new Date(previousData[previousData.length - 1].date).getTime() - new Date(previousData[0].date).getTime()
    }

    const isCurrentPrimary = currentRange >= previousRange
    const primaryDataset = isCurrentPrimary ? currentData : previousData

    const dataIterator = (data: any[]) => {
      return data.map((item, index) => ({
        date: new Date(item.date),
        displayDate: new Date(new Date(primaryDataset[0].date).getTime() + index * getTimeIncrement(selectedTimeScale)),
        rate: item?.prepaid_share?.toFixed(2) || 0,
        actualDate: new Date(item.date)
      }))
    }

    const mappedCurrentData = dataIterator(currentData)
    const mappedPreviousData = dataIterator(previousData)

    return {
      currentWeekData: mappedCurrentData,
      previousWeekData: mappedPreviousData,
      primaryDataset: isCurrentPrimary ? mappedCurrentData : mappedPreviousData,
      secondaryDataset: isCurrentPrimary ? mappedPreviousData : mappedCurrentData,
      isCurrentPrimary,
      currentDateRange: {
        from: new Date(currentData[0].date),
        to: new Date(currentData[currentData.length - 1].date)
      },
      previousDateRange:
        previousData.length > 0
          ? {
              from: new Date(previousData[0].date),
              to: new Date(previousData[previousData.length - 1].date)
            }
          : null,
      totalPrepaidSharePercentage: prepaidShareData,
      prepaidSharePercentageChange: prepaidShareData - prepaidShareComparedData
    }
  }, [data, selectedTimeScale])

  const currentDateRange = prepaidShareData.currentDateRange
  const previousDateRange = prepaidShareData.previousDateRange

  const currentDateRangeLegend =
    prepaidShareData.currentWeekData.length === 1
      ? format(currentDateRange.from, 'MMM dd, yyyy')
      : formatDateRange(currentDateRange.from, currentDateRange.to, selectedTimeScale)

  const previousDateRangeLegend = previousDateRange
    ? formatDateRange(previousDateRange.from, previousDateRange.to, selectedTimeScale)
    : null

  const renderComponent = () => {
    if (isLoading) {
      return <ChartSkeleton />
    } else if (isError) {
      return <div>Error</div>
    } else if (prepaidShareData.currentWeekData.length > 0) {
      return (
        <>
          <div className='flex items-center gap-2 my-2'>
            {/* <div className='text-[18px] font-bold text-carbon mb-1'>{totalPerpaidPercentage.toFixed(2)}%</div>
            <TrendIndicator value={prepaidSharePercentageChange} formatter={value => `${Math.abs(value)}%`} /> */}

            <div className='text-[18px] font-bold text-carbon mb-1'>
              {formatNumberToTwoDecimals(prepaidShareData.totalPrepaidSharePercentage)}%
            </div>
            <TrendIndicator
              value={formatNumberToTwoDecimals(prepaidShareData.prepaidSharePercentageChange)}
              formatter={value => `${Math.abs(value)}%`}
            />
          </div>
          <div className='w-full h-[84%]'>
            <ComparisonLineChart
              data={prepaidShareData}
              isNoComparison={false}
              selectedTimeScale={selectedTimeScale}
              tooltipTitle='Prepaid Share'
              thresholdAlertConfig={{
                isAlertsSupported: Boolean(threshold_alerts_supported),
                onClickAlert: (rate: number) => setAlertDialogState(true, WidgetId.payment_prepaid_share, rate)
              }}
            />
          </div>
          <div className='rounded-lg flex-1'>
            <div className='flex gap-6 mb-4 px-4 text-[#949494] text-xs'>
              <div className='flex items-center gap-2'>
                <div className='w-8 h-1 bg-primary-dark rounded-full'></div>
                <span>{currentDateRangeLegend}</span>
              </div>
              {previousDateRangeLegend && (
                <div className='flex items-center gap-2'>
                  <div className='w-8 h-1 border-t-2 border-dashed border-[#949494]'></div>
                  <span>{previousDateRangeLegend}</span>
                </div>
              )}
            </div>
          </div>
        </>
      )
    } else {
      return <NoData />
    }
  }

  return (
    <div ref={ref} className='w-full h-full'>
      {renderComponent()}
    </div>
  )
}
